import { useState, useContext, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import usePageTitle from "../../../hooks/use-page-title";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import AppUserContext from "../../../context/app-user-context";
import { ROUTES } from "../../../routes";
import { MdDelete, MdModeEditOutline, MdSearch } from "react-icons/md";
import { Dropdown } from "react-bootstrap";
import Debouncer from "../../../modules/debouncer/debouncer";
import Notifier from "../../../modules/notifier/notifier";
import ROLE, { getRoleLabel, getExistingRoleUrlParam } from "../../../modules/role";
import { CONST_SELECT_ROLES } from "../../../config/constants";
import ApiUser from "../../../modules/api/user";
import ServerSideUserDataTable from "../../../components/common/ServerSideUserDataTable";
import RoleFilterSelector from "../../../components/portal/RoleFilterSelector";
import ModulePopup from "../../../modules/popup/popup";

export default function PageUserList() {
  const title = "Manage Users";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);

  useEffect(() => {
    setCustomText(title);
  }, []);

  const user = useContext(AppUserContext);

  const ROLES_FILTER = useMemo(() => {
    let roles = CONST_SELECT_ROLES;
    if (user.role === ROLE.ADMIN) {
      roles = roles.filter(role => [ROLE.ADMIN, ROLE.PROGRAMME_ADMIN].some(value => value === role.value));
      roles.push({ label: "All", value: "" });
    } else {
      roles = roles.filter(role => [ROLE.STUDENT, ROLE.SUPERVISOR, ROLE.INDUSTRY_SUPERVISOR].some(value => value === role.value))
    }
    return roles;
  }, []);
  const [filterBy, setFilterBy] = useState("name");
  // get the query string param of "role", if value not found, fallback to `ROLE.STUDENT`
  const roleParam = useMemo(() => getExistingRoleUrlParam("role", ROLES_FILTER[0].value), []);
  const [role, setRole] = useState<string>(ROLES_FILTER.some(role => role.value === roleParam) ? roleParam : ROLES_FILTER[0].value);
  const [refresh, setRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

  return <main className="form-generic content-generic content-generic--fluid">

    {/* <h1 className="fs-title hide-mobile-down">{title}</h1> */}

    <div className="cg__actions">
      <div className="cg__action-buttons">
        <Link to={`${ROUTES.PORTAL_USERS}/register`} className="btn btn-secondary">
          Add New User
        </Link>
        {" "}
        <button className="btn btn-outline-dark"
          onClick={() => setRefresh(i => !i)}
        >
          Refresh
        </button>
        {" "}
        {selectedUsers.length > 0 && <button className="btn btn-outline-primary"
          onClick={(e) => {
            const deletedUserIds = selectedUsers.filter(i => typeof i._id !== "undefined").map(i => i._id);
            ModulePopup.showAlertConfirm({
              title: "Delete Confirmation",
              description: <>
                <p>Are you sure to delete {deletedUserIds.length} users?
                  <br/> This action can't be undone.
                </p>
              </>,
              onYes: () => Debouncer.execute("DELETE_USERS", async () => {
                ApiUser.remove(deletedUserIds).then(res => {
                  if (res.status) {
                    setSelectedUsers([]);
                    setRefresh(i => !i);
                    Notifier.success(res.message);
                  } else {
                    Notifier.error(res.message);
                  }
                });
              })
            })
          }}
        >
          Delete Selected
        </button>}
      </div>
    </div>

    <div className="cg__actions">
      <div className="cg__action-buttons">
        <RoleFilterSelector default={role} options={ROLES_FILTER} onChange={(v) => {
          // when changing role, we reset the search box
          setSearch("");
          setRole(v);
        }} />
      </div>
      <div className="cg__action-advance-search">
        <div className="cg__action-dropdown me-2">
          <select defaultValue={filterBy} onChange={(e) => {
            setFilterBy(e.target.value);
          }}>
            <option value="name">Name</option>
            <option value="email">Email</option>
          </select>
        </div>
        <div className="cg__action-search">
          <span>
            <MdSearch/>
          </span>
          <input type="text" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
    </div>

    <ServerSideUserDataTable
      refresh={refresh}
      api={ApiUser.pagingSearch}
      selectableRows
      onSelectedRowsChange={(e) => setSelectedUsers(e.selectedRows)}
      role={role}
      filterBy={filterBy}
      search={search}
      columns={[
        {
          id: "name",
          name: "Name",
          cell: (row) => row.name
        },
        {
          id: "role",
          name: "Role",
          omit: role !== "",
          cell: (row) => row.roles.map((r: string) => getRoleLabel(r)).join(", ")
        },
        {
          id: "organizationName",
          name: "Organization",
          cell: (row) => row.organizationName
        },
        {
          id: "programmeName",
          name: "Programme",
          cell: (row) => row.programmeName
        },
        {
            id: "managedProgrammeNames",
            name: "Managed Programmes",
            cell: (row) => row.managedProgrammeNames,
            omit: role !== ROLE.PROGRAMME_ADMIN,
        },
        {
          id: "email",
          name: "Email",
          cell: (row) => row.email
        },
        {
          name: "",
          button: true,
          cell: (row) => <div>
            <Dropdown align={"end"} drop={"down"}>
              <Dropdown.Toggle variant="secondary" id="dropdown-basic" className="btn-sm">
                Manage
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Link className="dropdown-item" to={`edit/${row._id}`}>
                  <MdModeEditOutline className="cursor-pointer icon-action-default" /> {" "} Edit
                </Link>

                <Dropdown.Divider />

                <button className="dropdown-item btn-danger" onClick={() => {
                  ModulePopup.showAlertConfirm({
                    title: "Delete Confirmation",
                    description: <>
                      <p>Are you sure to delete this user (<strong>{row.name}</strong>)?
                        <br/> This action can't be undone.
                      </p>
                    </>,
                    onYes: () => Debouncer.execute("DELETE_USER", async () => {
                      ApiUser.remove([row._id]).then(res => {
                        if (res.status) {
                          setRefresh(i => !i);
                          Notifier.success(res.message);
                        } else {
                          Notifier.error(res.message);
                        }
                      });
                    })
                  });
                }}>
                  <MdDelete/> {" "} Delete
                </button>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        }
      ]}
    />

  </main>;
}
